<script setup lang="ts">
import {
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
} from "@headlessui/vue";
import NavLink from "@/core/components/layout/NavLink.vue";
import IconManager from "@/core/IconManager.vue";
import { onMounted, ref } from "vue";
import { RoutePath } from "../../models/route-path";
import {
  logout,
  loadUserProfile,
  isAdmin,
} from "@/modules/auth/services/authService";
import { useI18n } from "vue-i18n";

const props = defineProps<{ modelValue: boolean; title?: string }>();

onMounted(async () => {
  profile.value = await loadUserProfile();
  canOpenAudit.value = isAdmin();
});

const emit = defineEmits<{ "update:modelValue": [boolean] }>();

const profile = ref<{ username?: string; email?: string } | null>(null);
const canOpenAudit = ref<boolean>(false);
const { t } = useI18n();
function close() {
  emit("update:modelValue", false);
}

function toggle() {
  emit("update:modelValue", !props.modelValue);
}

const handleLogout = () => {
  logout();
  close();
};
</script>

<template>
  <button
    @click="toggle"
    class="fixed top-16 sm:top-20 left-3 sm:left-4 z-[110] p-2 sm:p-3 bg-white shadow-lg rounded-md border hover:bg-gray-50 transition-all duration-200 lg:hover:scale-105"
    :class="{
      'left-[calc(28rem+0.75rem)] sm:left-[calc(24rem)]': modelValue,
      'shadow-xl': modelValue,
    }"
  >
    <svg
      class="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 transition-transform duration-200"
      :class="{ 'rotate-180': modelValue }"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M9 5l7 7-7 7"
      />
    </svg>
  </button>
  <TransitionRoot :show="modelValue" as="template">
    <Dialog class="relative z-[100]" @close="close">
      <TransitionChild
        enter="ease-in-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in-out duration-300"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black/40"></div>
      </TransitionChild>

      <div class="fixed inset-0 overflow-hidden">
        <div class="absolute inset-0 overflow-hidden">
          <div
            class="pointer-events-none fixed inset-y-0 left-0 flex max-w-full pr-10"
          >
            <TransitionChild
              enter="transform transition ease-in-out duration-300"
              enter-from="-translate-x-full"
              enter-to="translate-x-0"
              leave="transform transition ease-in-out duration-300"
              leave-from="translate-x-0"
              leave-to="-translate-x-full"
            >
              <DialogPanel class="pointer-events-auto w-screen max-w-sm h-full">
                <div class="flex h-full flex-col bg-white shadow-xl">
                  <div
                    class="px-6 py-4 bg-primary flex items-center justify-between"
                  >
                    <h2 class="text-lg font-bold text-white">{{ title }}</h2>
                    <button @click="close" class="text-white">✕</button>
                  </div>
                  <div class="flex-1 flex flex-col">
                    <!-- Scrollable content area -->
                    <div class="flex-1 p-3 sm:p-4 lg:p-6">
                      <h2
                        class="text-lg sm:text-xl font-semibold mb-4 text-gray-700"
                      >
                        {{ t("navigationDrawer.title") }}
                      </h2>

                      <nav class="flex flex-col gap-1 sm:gap-2">
                        <NavLink
                          :to="RoutePath.Reservations"
                          icon="CalendarCheck"
                          :label="t('navigationDrawer.reserve')"
                          @navigate="close"
                        />

                        <NavLink
                          icon="Calendar"
                          :label="t('navigationDrawer.events')"
                        >
                          <NavLink
                            :to="RoutePath.Events"
                            icon="Calendar"
                            :label="t('navigationDrawer.events')"
                            @navigate="close"
                          />

                          <NavLink
                            :to="RoutePath.Resources"
                            icon="Box"
                            :label="t('navigationDrawer.resources')"
                            @navigate="close"
                          />

                          <NavLink
                            :to="RoutePath.Occupation"
                            icon="ChartBar"
                            :label="t('navigationDrawer.occupation')"
                            @navigate="close"
                          />

                          <NavLink
                            :to="RoutePath.DashboardExecutive"
                            icon="File"
                            :label="t('navigationDrawer.executiveDashboard')"
                            @navigate="close"
                          />

                          <NavLink
                            :to="RoutePath.Dashboard"
                            icon="Dashboard"
                            :label="t('navigationDrawer.dashboard')"
                            @navigate="close"
                          />

                          <NavLink
                            :to="RoutePath.EventType"
                            icon="Gear"
                            :label="t('navigationDrawer.eventTypes')"
                            @navigate="close"
                          />
                        </NavLink>

                        <NavLink
                          icon="Users"
                          :label="t('navigationDrawer.users')"
                        >
                          <NavLink
                            :to="RoutePath.Users"
                            icon="Users"
                            :label="t('navigationDrawer.users')"
                            @navigate="close"
                          />

                          <NavLink
                            v-if="canOpenAudit"
                            :to="RoutePath.audit"
                            icon="ShieldCheck"
                            :label="t('navigationDrawer.auditUsers')"
                            @navigate="close"
                          />
                        </NavLink>
                      </nav>
                    </div>

                    <!-- User Section -->
                    <div
                      class="flex flex-col gap-2 py-2 px-6 bg-background border-t-light-gray-1 text-gray-700 font-medium text-base"
                    >
                      <div class="flex flex-row gap-2 items-center">
                        <p>{{ t("common.username") }}:</p>
                        <p class="truncate">
                          {{ profile?.username }}
                        </p>
                      </div>

                      <div class="flex flex-row gap-2 items-center">
                        <p>{{ t("common.email") }}:</p>
                        <p class="truncate">
                          {{ profile?.email }}
                        </p>
                      </div>

                      <button
                        @click="handleLogout"
                        class="flex flex-row items-center gap-1 text-red-500 font-bold hover:text-red-400 transition-colors duration-150 w-fit"
                      >
                        <IconManager name="Logout" />
                        <span> {{ t("common.signOut") }}</span>
                      </button>
                    </div>
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
